import React, { useEffect, useState } from "react";
import { TableColumn, TableRow, ToxnotReactTable } from '../ToxnotReactTable';
import { ToxnotReactModal, ToxnotReactModalBody, ToxnotReactModalFooter } from '../ToxnotReactModal';
import { TopActionButtons } from '../TopActionsAndScrollableContentContainer';
import TextInput from "../TextInput";
import { DropdownMenu } from "../DropdownMenu"; // Added import
import TextAreaInput from "../TextAreaInput"; // Added import
import { ToxnotServiceClient } from "../../../store/ToxnotServiceClient";

interface SmelterListProps {
    value: string;
    localizer: any;
    onValueChange: (value: Smelter[]) => void;
    isDisabled?: boolean;
}

interface CountryData {
    [code: string]: string;
};

interface SmelterList {
    Smelter: Smelter[]
};

interface SmelterLookupData {
    SmelterID: string;
    "Standard Smelter Name": string | null;
    "Smelter Alias": string | null;
    Mineral: string;
    Country: string;
    City: string;
    "Smelter State or Province": string;
    Street: string;
}

interface Smelter {
    City?: string;
    Comments?: string;
    ContactEmail?: string;
    ContactName?: string;
    Country?: string;
    DoesAllFeedstockComeFromScrapOrRecycled?: string;
    Id: string;
    IdNumber?: string;
    IdSource?: string;
    Metal?: string;
    MetalSource?: string;
    MineCountry?: string;
    MineName?: string;
    NextSteps?: string;
    ReferenceName?: string;
    State?: string;
    StreetAddress?: string;
}

const SmelterList: React.FC<SmelterListProps> = ({
    value,
    localizer,
    onValueChange,
    isDisabled = false,
}) => {
    const toxnotServiceClient = new ToxnotServiceClient();

    const ChemicalNameMap: Record<string, string> = {
        "Ta": "Tantalum (Ta)",
        "Sn": "Tin (Sn)",
        "Au": "Gold (Au)",
        "W": "Tungsten (W)"
    };

    // Reverse mapping for converting full names to codes
    const MineralToCodeMap: Record<string, string> = {
        "Tantalum": "Ta",
        "Tin": "Sn",
        "Gold": "Au",
        "Tungsten": "W"
    };

    const getDisplayName = (chemicalName: string): string => {
        return ChemicalNameMap[chemicalName] || chemicalName;
    };
    // Function to convert full mineral name to chemical code
    const getMineralCode = (mineralName: string): string => {
        if (!mineralName) return "";
        // Try exact match first
        const exactMatch = MineralToCodeMap[mineralName];
        if (exactMatch) return exactMatch;

        // Try case-insensitive match
        const lowerCaseName = mineralName.toLowerCase();
        for (const [fullName, code] of Object.entries(MineralToCodeMap)) {
            if (fullName.toLowerCase() === lowerCaseName) {
                return code;
            }
        }

        return "";
    };

    // Function to get country code from country full name using existing countriesWithCodes
    const getCountryCodeByFullName = (countryName: string): string => {
        if (!countriesWithCodes) return countryName;

        // Find the country code where the full name matches
        const matchingEntry = Object.entries(countriesWithCodes).find(([code, name]) => {
            return name.toUpperCase() === countryName.toUpperCase();
        });

        return matchingEntry ? matchingEntry[0] : countryName;
    };

    const emptySmelter = JSON.parse('{"Metal":"","ReferenceName":"","Country":"","Id":"","IdNumber":"","IdSource":"","MetalSource":"","StreetAddress":"","City":"","State":"","ContactName":"","ContactEmail":"","NextSteps":"","MineName":"","MineCountry":"","DoesAllFeedstockComeFromScrapOrRecycled":"","Comments":""}');
    const smelterFieldKeys: string[] = [
        "IdNumber", "Metal", "ReferenceName", "StandardName", "Country","Id", "IdSource",
        "StreetAddress", "City", "State", "ContactName", "ContactEmail", "NextSteps", "MetalSource", "MineName", "MineCountry",
        "DoesAllFeedstockComeFromScrapOrRecycled", "Comments"
    ];
    const [smelter, setSmelter] = useState<Smelter>(emptySmelter);
    const [smelterIdsToDelete, setSmelterIdsToDelete] = useState({});
    const [tableItemId, setTableItemId] = useState<string>("");
    const [smelters, setSmelters] = useState<Smelter[]>([]);
    const [countriesWithCodes, setCountriesWithCodes] = useState<CountryData>();
    const [smeltersLookupData, setSmeltersLookupData] = useState<SmelterLookupData[]>([]);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [selectedSmelterLookup, setSelectedSmelterLookup] = useState<string>("");

    useEffect(() => {
        let isMounted = true; // Flag to track if component is mounted
        
        const getCountries = async () => {
            const result: any = await toxnotServiceClient.FetchCountries() as any;
            // Check if component is still mounted AFTER the async call completes
            if (isMounted) {
                const countryData: CountryData = result?.data as CountryData;
                setCountriesWithCodes(countryData);
            }
        };
        
        const getSmelters = async () => {
            const result: any = await toxnotServiceClient.FetchSmelters() as any;
            // Check if component is still mounted AFTER the async call completes
            if (isMounted) {
                const smeltersData: SmelterLookupData[] = result?.data as SmelterLookupData[];
                setSmeltersLookupData(smeltersData ?? []);
            }
        };
        
        getCountries();
        getSmelters();
        
        // Parse smelters data
        const parsedSmelters = JSON.parse(value && value.trim() !== "" ? value : "[]");
        if (isMounted) {
            setSmelters(parsedSmelters ?? []);
        }
        
        // Cleanup function that runs when component unmounts
        return () => {
            isMounted = false; // Update flag when component unmounts
        };
    }, [value]);

    
    const getCountryNameByCode = (code: string): string => {
        return localizer.localizeString(countriesWithCodes ? countriesWithCodes[code.toUpperCase()] || "Unknown Country" : "Unknown Country");
    };

    const getSmelterListHeaderRow = (): TableColumn<Smelter>[] => {
        return [
            {
                value: localizer.localizeString("Smelter ID")
            },
            {
                value: localizer.localizeString("Metal")
            },
            {
                value: localizer.localizeString("Smelter Name")
            },
            {
                value: localizer.localizeString("Smelter Country")
            },
            {
                value: localizer.localizeString("Source")
            }
        ];
    }

    const getSmelterListRow = (smelter: Smelter, index: number): TableRow => {
        return {
            cols: [
                smelter.IdNumber ?? localizer.localizeString("Unknown Smelter ID"),
                getDisplayName(smelter.Metal ?? localizer.localizeString("Unknown Metal")),
                smelter.ReferenceName ?? localizer.localizeString("Unknown Smelter Name"),
                getCountryNameByCode(smelter.Country ?? ""),
                smelter.IdSource ?? localizer.localizeString("Unknown Source")
            ]
        } as TableRow;
    }

    const handleSmelterRowClick = (e: React.MouseEvent, smelter: Smelter): void => {
        const smelterKey = `${smelter.IdNumber}-${smelter.Metal ?? ""}-${smelter.Country ?? ""}`;
        
        setSmelter(smelter);
        setTableItemId(smelterKey);
        setIsEditMode(true);
        // Restore the selected smelter lookup if the smelter has an Id
        setSelectedSmelterLookup(smelter.Id || "");
    
        $('#smelter-details-modal-v2').modal('show');
    };
    

    const onTableInputValueChange = (fieldName: string, newValue: string, type: string) => {
        const updatedSmelter = { ...smelter };
        updatedSmelter[fieldName as keyof typeof updatedSmelter] = newValue;
        
        // When Smelter Identification (Id) changes, also update IdNumber to match
        if (fieldName === 'Id') {
            updatedSmelter.IdNumber = newValue;
        }
        
        setSmelter(updatedSmelter);
    };

    // Helper function for country dropdown options
    const getCountryOptions = () => {
        if (!countriesWithCodes) return [{ value: "", name: localizer.localizeString("Choose one") }];
        return [
            { value: "", name: localizer.localizeString("Choose one") },
            ...Object.entries(countriesWithCodes)
                .map(([code, name]) => ({ value: code, name: localizer.localizeString(name) }))
                .sort((a, b) => a.name.localeCompare(b.name)) 
        ];
    };

    // Helper function for simple dropdown options
    const getSimpleOptions = (options: string[]) => {
        return [
            { value: "", name: localizer.localizeString("Choose one") },
            ...options.map(opt => ({ value: opt, name: localizer.localizeString(opt) }))
        ];
    };

    const getMetalOptions = () => {
        return [
            { value: "", name: localizer.localizeString("Choose one") },
            { value: "Sn", name: "Tin (Sn)" },
            { value: "Ta", name: "Tantalum (Ta)" },
            { value: "Au", name: "Gold (Au)" },
            { value: "W", name: "Tungsten (W)" }
        ];
    };

    // Define static options
    const metalOptions = getMetalOptions();
    const yesNoUnknownOptions = getSimpleOptions(["Unknown", "Yes", "No"]);
    
    // Helper function for smelter lookup dropdown options
    const getSmelterLookupOptions = () => {
        if (!smeltersLookupData || smeltersLookupData.length === 0) {
            return [{ value: "", name: localizer.localizeString("Choose one") }];
        }
        return [
            { value: "", name: localizer.localizeString("Choose one") },
            ...smeltersLookupData.map(s => ({
                value: s.SmelterID,
                name: s["Smelter Alias"] || s["Standard Smelter Name"]  || "",
                smelterData: s
            }))
        ];
    };

    const smelterLookupOptions = getSmelterLookupOptions();

    // Handler for smelter lookup selection
    const handleSmelterLookupChange = (selectedOption: any) => {
        if (selectedOption.value && selectedOption.smelterData) {
            const smelterData = selectedOption.smelterData;
            // Set the Smelter Identification field with the SmelterID and auto-populate address fields
            const updatedSmelter = {
                ...smelter,
                Id: selectedOption.value,
                IdNumber: selectedOption.value,
                Country: getCountryCodeByFullName(smelterData.Country) || smelter.Country,
                City: smelterData["Smelter City"] || smelter.City,
                StreetAddress: smelterData.Location || smelter.StreetAddress,
                State: smelterData["Smelter State or Province"] || smelter.State,
                ReferenceName: smelterData["Standard Smelter Name"] || "",
                Metal: getMineralCode(smelterData.Mineral || "")
            };
            setSmelter(updatedSmelter);
            // Track the selected smelter to show it in the dropdown
            setSelectedSmelterLookup(selectedOption.value);
        } else {
            // When "Choose one" is selected, clear all smelter details that were auto-populated
            const updatedSmelter = {
                ...smelter,
                Id: "",
                IdNumber: "",
                ReferenceName: "",
                Country: "",
                City: "",
                StreetAddress: "",
                State: "",
                Metal: ""
            };
            setSmelter(updatedSmelter);
            setSelectedSmelterLookup("");
        }
    };


    const renderSmelterDetailsTableHeaderRow = () => {
        return (
            <thead>
                <tr id="headerrow">
                    <th>
                        {localizer.localizeString("Smelter Detail")}
                    </th>
                    <th>
                        {localizer.localizeString("Value")}
                    </th>
                </tr>
            </thead>
        )
    }


    const renderSmelterDetailsTableBody = () => {
        return (
            <tbody>
                {smelterFieldKeys.map(fieldKey => {
                    let propValue = (smelter as any)[fieldKey];

                    if (propValue === null || propValue === undefined) {
                        propValue = "";
                    } else if (typeof propValue !== "string") {
                        propValue = "Unknown";
                    }

                    return (
                        <tr key={fieldKey}>
                            <td><b>{localizer.localizeString(fieldKey === "IdNumber" ? `${fieldKey} (*)` : fieldKey)}</b></td>
                            <td><TextInput value={fieldKey === "Country" ? getCountryNameByCode(propValue) : propValue}
                                onValueChange={(newValue) => onTableInputValueChange(fieldKey, newValue, "Smelter")}
                                additionalClass={fieldKey === 'IdNumber' && propValue === '' ? 'highlighted-input' : ''} />
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        )
    }

    const createSmelterButton_click = () => {
        setSmelter(emptySmelter);
        setTableItemId("");
        setIsEditMode(false);
        setSelectedSmelterLookup("");
        $('#smelter-details-modal-v2').modal('show');
    }

    const onModalCancel = () => {
        setSmelter(emptySmelter);
        setTableItemId("");
        setIsEditMode(false);
        setSelectedSmelterLookup("");
    }

    const onModalSave = () => {
        if (!smelter.Id?.trim().length) return;
    
        setSmelters((prevSmelters) => {
            let updatedSmelters = [...prevSmelters];
    
            const previousKey = tableItemId;
    
            const existingIndex = updatedSmelters.findIndex(s => 
                `${s.IdNumber}-${s.Metal ?? ""}-${s.Country ?? ""}` === previousKey
            );
    
            if (existingIndex > -1) {
                updatedSmelters[existingIndex] = smelter;
            } else {
                updatedSmelters.push(smelter);
            }
    
            onValueChange(updatedSmelters);
    
            return updatedSmelters;
        });

        setSmelter(emptySmelter);
        setTableItemId("");
        setIsEditMode(false);
        setSelectedSmelterLookup("");
        $('#smelter-details-modal-v2').modal('hide');
    };
    
    

    const getSmelterId = (model: Smelter) => {
        return model.IdNumber ?? "Unknown Smelter ID";
    }

    const updateSelectedSmelters = (selectedIds: { [id: string]: string }) => {
        setSmelterIdsToDelete({ ...selectedIds });
    }

    const deleteSmelterButton_click = (e: React.MouseEvent) => {
        e.nativeEvent.stopImmediatePropagation();
        $('#warn-about-deleting-smelters-modal-v2').modal('show');
    }

    const closeDeleteSmeltersWarningModal_click = (e: React.MouseEvent) => {
        e.nativeEvent.stopImmediatePropagation();
        $('#warn-about-deleting-smelters-modal-v2').modal('hide');
    }

    const getSmelters = () => {
        return smelters;
    }

    const deleteSmeltersButtonConfirm_click = () => {
        setSmelters((prevSmelters) => {
            const selectedIds = new Set(Object.keys(smelterIdsToDelete));
            const updatedSmelters = prevSmelters.filter(s => !selectedIds.has(s.IdNumber??""));
            onValueChange(updatedSmelters);
    
            return updatedSmelters;
        });
        setSmelterIdsToDelete({});
        $('#warn-about-deleting-smelters-modal-v2').modal('hide');
    };
    
    return (
        <div>
            <h4><strong>{localizer.localizeString("Smelter List")}</strong></h4>
            <TopActionButtons>
                <div>
                    <button className="btn btn-primary" onClick={createSmelterButton_click}>
                        <span className="glyphicon glyphicon-plus" /> {localizer.localizeString("Add Smelter")}
                    </button>
                    <button className="btn btn-danger u-ml-8" onClick={deleteSmelterButton_click} disabled={Object.keys(smelterIdsToDelete).length === 0}>
                        <span className="glyphicon glyphicon-remove" /> {localizer.localizeString("Delete Smelter")}
                    </button>
                </div>
            </TopActionButtons>
            <div id="smelter-list-table" className="tn-section-padding-container">
                <ToxnotReactTable
                    allRowModels={getSmelters()}
                    localizer={localizer}
                    headerRow={getSmelterListHeaderRow()}
                    getRowForTable={getSmelterListRow}
                    rowClickEventHandler={handleSmelterRowClick}
                    selectingRowOptions={{
                        rowModelGetId: getSmelterId,
                        onRowSelected: updateSelectedSmelters
                    }}
                    enablePaging={true}
                    fullSectionWidth={true}
                    selectAllAcrossAllPages={true}
                />
            </div>

            <ToxnotReactModal modalId={"smelter-details-modal-v2"} localizedTitle={isEditMode ? localizer.localizeString("Edit Smelter") : localizer.localizeString("Create Smelter")}>
                <ToxnotReactModalBody>
                    <table id="createTableSmelter-v2" className="table table-bordered">
                        {renderSmelterDetailsTableHeaderRow()}
                        <tbody>
                            <tr>
                                <td><b>{localizer.localizeString("Metal (*)")}</b></td>
                                <td>
                                    <DropdownMenu
                                        options={metalOptions}
                                        value={metalOptions.find(opt => opt.value === (smelter.Metal ?? "")) ?? metalOptions[0]} // Find the object
                                        onOptionSelected={(selectedOption) => onTableInputValueChange("Metal", selectedOption.value, "Smelter")} // Extract value
                                        getFriendlyNameForOptions={opt => opt.name}
                                        getUniqueValueForOption={opt => opt.value}
                                        // Removed additionalClass="form-control"
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td><b>{localizer.localizeString("Smelter Lookup")}</b></td>
                                <td>
                                    <DropdownMenu
                                        options={smelterLookupOptions}
                                        value={smelterLookupOptions.find(opt => opt.value === selectedSmelterLookup) ?? smelterLookupOptions[0]}
                                        onOptionSelected={handleSmelterLookupChange}
                                        getFriendlyNameForOptions={opt => opt.name}
                                        getUniqueValueForOption={opt => opt.value}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td><b>{localizer.localizeString("Smelter Name")}</b></td>
                                <td><TextInput value={smelter.ReferenceName ?? ""} onValueChange={(newValue) => onTableInputValueChange("ReferenceName", newValue, "Smelter")} additionalClass="form-control" /></td>
                            </tr>
                            <tr>
                                <td><b>{localizer.localizeString("Smelter Country (*)")}</b></td>
                                <td>
                                    <DropdownMenu
                                        options={getCountryOptions()}
                                        value={getCountryOptions().find(opt => opt.value === (smelter.Country ?? "")) ?? getCountryOptions()[0]} // Find the object
                                        onOptionSelected={(selectedOption) => onTableInputValueChange("Country", selectedOption.value, "Smelter")} // Extract value
                                        getFriendlyNameForOptions={opt => opt.name}
                                        getUniqueValueForOption={opt => opt.value}
                                        // Removed additionalClass="form-control"
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td><b>{localizer.localizeString("Smelter Identification (*)")}</b></td>
                                <td><TextInput value={smelter.Id ?? ""} onValueChange={(newValue) => onTableInputValueChange("Id", newValue, "Smelter")} additionalClass="form-control" /></td>
                            </tr>
                             <tr>
                                <td><b>{localizer.localizeString("Smelter Source")}</b></td>
                                <td><TextInput value={smelter.IdSource ?? ""} onValueChange={(newValue) => onTableInputValueChange("IdSource", newValue, "Smelter")} additionalClass="form-control" /></td>
                            </tr>
                            <tr>
                                <td><b>{localizer.localizeString("Smelter Street")}</b></td>
                                <td><TextInput value={smelter.StreetAddress ?? ""} onValueChange={(newValue) => onTableInputValueChange("StreetAddress", newValue, "Smelter")} additionalClass="form-control" /></td>
                            </tr>
                            <tr>
                                <td><b>{localizer.localizeString("Smelter City")}</b></td>
                                <td><TextInput value={smelter.City ?? ""} onValueChange={(newValue) => onTableInputValueChange("City", newValue, "Smelter")} additionalClass="form-control" /></td>
                            </tr>
                            <tr>
                                <td><b>{localizer.localizeString("Smelter Facility Location")}</b></td>
                                <td><TextInput value={smelter.State ?? ""} onValueChange={(newValue) => onTableInputValueChange("State", newValue, "Smelter")} additionalClass="form-control" /></td>
                            </tr>
                            <tr>
                                <td><b>{localizer.localizeString("Smelter Contact Name")}</b></td>
                                <td><TextInput value={smelter.ContactName ?? ""} onValueChange={(newValue) => onTableInputValueChange("ContactName", newValue, "Smelter")} additionalClass="form-control" /></td>
                            </tr>
                            <tr>
                                <td><b>{localizer.localizeString("Smelter Contact Email")}</b></td>
                                <td><TextInput value={smelter.ContactEmail ?? ""} onValueChange={(newValue) => onTableInputValueChange("ContactEmail", newValue, "Smelter")} additionalClass="form-control" /></td>
                            </tr>
                            <tr>
                                <td><b>{localizer.localizeString("Proposed Next Steps")}</b></td>
                                <td><TextInput value={smelter.NextSteps ?? ""} onValueChange={(newValue) => onTableInputValueChange("NextSteps", newValue, "Smelter")} additionalClass="form-control" /></td>
                            </tr>
                            <tr>
                                <td><b>{localizer.localizeString("Name of Mine(s) or if recycled or scrap sourced enter 'recycled' or scrap")}</b></td>
                                <td><TextInput value={smelter.MineName ?? ""} onValueChange={(newValue) => onTableInputValueChange("MineName", newValue, "Smelter")} additionalClass="form-control" /></td>
                            </tr>
                            <tr>
                                <td><b>{localizer.localizeString("Location (Country) of Mine(s) or if recycled or scrap sourced enter 'recycled' or scrap")}</b></td>
                                <td><TextInput value={smelter.MineCountry ?? ""} onValueChange={(newValue) => onTableInputValueChange("MineCountry", newValue, "Smelter")} additionalClass="form-control" /></td>
                            </tr>
                            <tr>
                                <td><b>{localizer.localizeString("Does 100% of the feedstock come from scrap or recycled sources?")}</b></td>
                                 <td>
                                    <DropdownMenu
                                        options={yesNoUnknownOptions}
                                        value={yesNoUnknownOptions.find(opt => opt.value === (smelter.DoesAllFeedstockComeFromScrapOrRecycled ?? "")) ?? yesNoUnknownOptions[0]} // Find the object
                                        onOptionSelected={(selectedOption) => onTableInputValueChange("DoesAllFeedstockComeFromScrapOrRecycled", selectedOption.value, "Smelter")} // Extract value
                                        getFriendlyNameForOptions={opt => opt.name}
                                        getUniqueValueForOption={opt => opt.value}
                                        // Removed additionalClass="form-control"
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td><b>{localizer.localizeString("Comments")}</b></td>
                                <td><TextAreaInput value={smelter.Comments ?? ""} onValueChange={(newValue) => onTableInputValueChange("Comments", newValue, "Smelter")} /></td>{/* Removed className="form-control" */}
                            </tr>
                        </tbody>
                    </table>
                </ToxnotReactModalBody>
                <ToxnotReactModalFooter localizer={localizer} onCancel={onModalCancel}>
                    <button type="button" className="btn btn-toxnot-primary"
                        data-dismiss="modal"
                        onClick={() => onModalSave()}
                        disabled={!smelter.Id?.trim().length || !smelter.Metal?.length || !smelter.Country?.length}>
                        {localizer.localizeString(isEditMode ? "Save" : "Add")}
                    </button>
                </ToxnotReactModalFooter>
            </ToxnotReactModal>

            <ToxnotReactModal modalId={"warn-about-deleting-smelters-modal-v2"} localizedTitle={localizer.localizeString("Are you sure you want to delete?")}>
                <ToxnotReactModalBody>
                    <div className="alert alert-warning">
                        <p>{localizer.localizeString("You are about to delete smelter(s). This will remove them from the Exchange.")}</p>
                        <p>{localizer.localizeString("Are you sure you want to continue?")}</p>
                    </div>
                </ToxnotReactModalBody>
                <ToxnotReactModalFooter localizer={localizer} enabledCloseButton={false}>
                    <button className="btn btn-default" onClick={closeDeleteSmeltersWarningModal_click}>{localizer.localizeString("Cancel")}</button>
                    <button className="btn btn-danger" onClick={deleteSmeltersButtonConfirm_click}>{localizer.localizeString("Yes, Delete")}</button>
                </ToxnotReactModalFooter>
            </ToxnotReactModal>
        </div>
    );
};

export default SmelterList;